/* One cell as the Data Table draws it, by its column's type: the first column's name beside
   an initials avatar, a choice as a tag in its tone, a link as chips of the rows it names, an
   email with the check of whoever verified it, a count as a quiet chip. */
import React from "react";
import { BadgeCheck, Check } from "lucide-react";

import InitialsAvatar from "@/shared/components/InitialsAvatar";
import { cn } from "@/shared/lib/cn";

import { shortDate } from "./columns";

import type { SpaceColumn, SpaceLinked, SpaceRow, SpaceTone } from "@/shared/types";

const TONE: Record<SpaceTone, string> = {
  blue: "bg-[var(--tone-blue-bg)] text-[var(--tone-blue-fg)] ring-[var(--tone-blue-ring)]",
  violet: "bg-[var(--tone-violet-bg)] text-[var(--tone-violet-fg)] ring-[var(--tone-violet-ring)]",
  pink: "bg-[var(--tone-pink-bg)] text-[var(--tone-pink-fg)] ring-[var(--tone-pink-ring)]",
  green: "bg-[var(--tone-green-bg)] text-[var(--tone-green-fg)] ring-[var(--tone-green-ring)]",
  amber: "bg-[var(--tone-amber-bg)] text-[var(--tone-amber-fg)] ring-[var(--tone-amber-ring)]",
  gray: "bg-[var(--tone-gray-bg)] text-[var(--tone-gray-fg)] ring-[var(--tone-gray-ring)]",
};
const TONES = Object.keys(TONE) as SpaceTone[];

/** The same name always gets the same tone, so a person or a linked row is recognisable. */
export function toneOf(name: string): SpaceTone {
  let sum = 0;
  for (const char of name) sum = (sum * 31 + char.charCodeAt(0)) >>> 0;
  return TONES[sum % TONES.length];
}

export function Tag({ name, tone }: { name: string; tone: SpaceTone }) {
  return (
    <span className={cn("inline-block max-w-full truncate align-middle px-[9px] py-[2px] rounded-[7px] text-[13px] font-[450] leading-5 ring-1 ring-inset", TONE[tone])}>
      {name}
    </span>
  );
}

/** A row another row names: its first letter on a tile, then its name. */
export function LinkChip({ linked }: { linked: SpaceLinked }) {
  const name = linked.label || "Untitled";
  return (
    <span className="inline-flex items-center gap-1.5 h-6 max-w-full pl-[3px] pr-2 rounded-[7px] bg-[var(--dt-card)] ring-1 ring-inset ring-[var(--dt-border)] text-[13px] text-[var(--dt-text)] align-middle">
      <span className={cn("w-[18px] h-[18px] shrink-0 grid place-items-center rounded-[5px] text-[10px] font-semibold", TONE[toneOf(name)])}>
        {name[0]?.toUpperCase()}
      </span>
      <span className="truncate">{name}</span>
    </span>
  );
}

/** A thing's first letter on a square tile in its tone: a company, a subject, a service. */
function LetterTile({ name }: { name: string }) {
  return (
    <span aria-hidden="true" className={cn("w-5 h-5 shrink-0 grid place-items-center rounded-[6px] text-[10.5px] font-semibold", TONE[toneOf(name)])}>
      {name[0]?.toUpperCase() ?? "?"}
    </span>
  );
}

export default function Cell({ column, row, first, people, rowsArePeople }: {
  column: SpaceColumn;
  row: SpaceRow;
  /** The table's first column: it names the row. */
  first: boolean;
  people: Record<string, string | null>;
  /** The rows are people (the table is unique by email): their names get a round avatar. */
  rowsArePeople: boolean;
}) {
  const value = row.values[column.key];
  if (first) {
    const name = value === null || value === undefined || value === "" ? "" : String(value);
    return (
      <span className="flex items-center gap-2.5 min-w-0">
        {rowsArePeople ? <InitialsAvatar name={name || "?"} size={24} /> : <LetterTile name={name || "?"} />}
        <span className={cn("truncate", name ? "text-[var(--dt-ink)]" : "text-[var(--dt-faint)]")}>{name || "Untitled"}</span>
      </span>
    );
  }
  if (value === null || value === undefined || value === "" || (Array.isArray(value) && !value.length)) {
    return column.type === "count" ? <span className="text-[var(--dt-faint)]">—</span> : null;
  }
  switch (column.type) {
    case "choice": {
      const choice = column.options.choices?.find((c) => c.name === value);
      return <Tag name={String(value)} tone={choice?.tone ?? "gray"} />;
    }
    case "link":
      return (
        <span className="flex items-center gap-1 min-w-0 overflow-hidden">
          {(value as SpaceLinked[]).map((linked) => <LinkChip key={linked.id} linked={linked} />)}
        </span>
      );
    case "count":
      return (
        <span className="inline-flex items-center h-6 px-2 rounded-[7px] ring-1 ring-inset ring-[var(--dt-border)] text-[13px] text-[var(--dt-muted)] tabular-nums">
          {Number(value).toLocaleString("en-US")}
        </span>
      );
    case "person": {
      const name = people[String(value)] || "Someone";
      return (
        <span className="inline-flex items-center gap-2 min-w-0 text-[var(--dt-text)]">
          <InitialsAvatar name={name} size={18} />
          <span className="truncate">{name}</span>
        </span>
      );
    }
    case "email":
      return (
        <span className="inline-flex items-center gap-1.5 min-w-0 text-[var(--dt-text)]">
          <span className="truncate">{String(value)}</span>
          {row.verified_by[column.key] && (
            <span title={`Verified by ${row.verified_by[column.key]}`} className="shrink-0 inline-flex">
              <BadgeCheck className="w-3.5 h-3.5 text-[var(--success)]" strokeWidth={2} aria-label={`Verified by ${row.verified_by[column.key]}`} />
            </span>
          )}
        </span>
      );
    case "url": {
      const href = /^https?:\/\//i.test(String(value)) ? String(value) : `https://${value}`;
      return (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          tabIndex={-1}
          className="text-[var(--dt-text)] underline decoration-[var(--dt-underline)] underline-offset-[3px] hover:text-[var(--dt-ink)] hover:decoration-current transition-colors"
        >
          {String(value).replace(/^https?:\/\//i, "")}
        </a>
      );
    }
    case "boolean":
      return value ? <Check className="w-4 h-4 text-[var(--dt-ink)]" strokeWidth={2} aria-label="Yes" /> : null;
    case "number":
      return <span className="text-[var(--dt-text)] tabular-nums">{Number(value).toLocaleString("en-US")}</span>;
    case "date":
      return <span className="text-[var(--dt-text)] tabular-nums">{shortDate(String(value))}</span>;
    case "datetime":
      return <span className="text-[var(--dt-text)] tabular-nums">{shortDate(String(value), true)}</span>;
    default:
      return <span className="text-[var(--dt-text)]">{String(value)}</span>;
  }
}
